/**
 * API client for the `roles` backend resource (RBAC) plus the current-user
 * profile lookup.
 *
 * Roles bundle a set of permission strings and are scoped by a `guard_name`
 * ('admin' for the admin panel, 'public' for the public website). The catalogue
 * of assignable permissions is fetched separately, grouped by module.
 *
 * Most endpoints return the standard `{ success, data }` envelope, which these
 * helpers unwrap. Note `getMyProfile` hits `/auth/me`, not `/roles`.
 */
import { apiClient } from './client';

// --- Types ---

/** A single permission, identified by its `name` (e.g. `"teams.create"`). */
export interface Permission {
  name: string;
}

/** Permissions bucketed by module/feature `group`, as returned by `getPermissions`. */
export interface PermissionGroup {
  group: string;
  permissions: Permission[];
}

/**
 * A role record. `permissions` is a flat array of permission name strings.
 * `guard_name` separates admin-panel roles from public-website roles.
 */
export interface Role {
  _id: string;
  name: string;
  name_ar?: string;
  name_en?: string;
  guard_name: 'admin' | 'public';
  permissions: string[];
  status?: 'active' | 'inactive';
  is_sign_up_role?: boolean;
  role_type?: 'school' | 'playground' | 'regional';
  image_url?: string;
  can_manage_teams?: boolean;
  is_invite_only?: boolean;
  can_invite?: boolean;
  role_category?: 'general' | 'playground';
  max_per_region?: number;
  max_per_playground?: number;
  has_admin_access?: boolean;
  has_dashboard_access?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Payload for creating a role; only `name` is required. */
export interface CreateRoleDto {
  name: string;
  name_ar?: string;
  name_en?: string;
  permissions?: string[];
  guard_name?: 'admin' | 'public';
  is_sign_up_role?: boolean;
  role_type?: 'school' | 'playground' | 'regional';
  image_url?: string;
  can_manage_teams?: boolean;
  is_invite_only?: boolean;
  can_invite?: boolean;
  role_category?: 'general' | 'playground';
  max_per_region?: number;
  max_per_playground?: number;
  has_admin_access?: boolean;
  has_dashboard_access?: boolean;
}

/** Payload for updating a role; all fields optional (partial update). */
export interface UpdateRoleDto {
  name?: string;
  name_ar?: string;
  name_en?: string;
  permissions?: string[];
  guard_name?: 'admin' | 'public';
  is_sign_up_role?: boolean;
  role_type?: 'school' | 'playground' | 'regional';
  image_url?: string;
  can_manage_teams?: boolean;
}

// --- API Calls ---

/**
 * Get all permissions grouped by module.
 *
 * GET `/roles/permissions`.
 * @returns the full permission catalogue, grouped by module.
 */
export async function getPermissions(): Promise<PermissionGroup[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PermissionGroup[] }>('/roles/permissions');
  return data.data;
}

/**
 * Get all roles.
 *
 * GET `/roles` with query params `guard_name` (and `search` when provided).
 * @param search - optional name filter; omitted from the query when falsy.
 * @param guardName - which guard to list; defaults to `'admin'`.
 * @returns the matching roles.
 */
export async function getRoles(search?: string, guardName?: string, roleCategory?: string): Promise<Role[]> {
  const { data } = await apiClient.get<{ success: boolean; data: Role[] }>('/roles', {
    params: search ? { search, guard_name: guardName } : { guard_name: guardName },
  });
  return data.data;
}

/**
 * Get a single role by ID.
 *
 * GET `/roles/:id`.
 * @param id - role id.
 * @returns the role.
 */
export async function getRole(id: string): Promise<Role> {
  const { data } = await apiClient.get<{ success: boolean; data: Role }>(`/roles/${id}`);
  return data.data;
}

/**
 * Create a new role.
 *
 * POST `/roles`.
 * @param dto - the new role's fields.
 * @returns the created role.
 */
export async function createRole(dto: CreateRoleDto): Promise<Role> {
  const { data } = await apiClient.post<{ success: boolean; data: Role }>('/roles', dto);
  return data.data;
}

/**
 * Update an existing role.
 *
 * PATCH `/roles/:id`.
 * @param id - role id.
 * @param dto - the fields to change.
 * @returns the updated role.
 */
export async function updateRole(id: string, dto: UpdateRoleDto): Promise<Role> {
  const { data } = await apiClient.patch<{ success: boolean; data: Role }>(`/roles/${id}`, dto);
  return data.data;
}

/**
 * Delete a role.
 *
 * DELETE `/roles/:id`.
 * @param id - role id.
 */
export async function deleteRole(id: string): Promise<void> {
  await apiClient.delete(`/roles/${id}`);
}

/**
 * Upload an icon/avatar image for a role.
 *
 * POST `/roles/:roleId/image` as `multipart/form-data` with the file under the
 * `file` field.
 * @param roleId - role id to attach the image to.
 * @param file - the image file to upload.
 * @returns the stored image URL.
 */
export async function uploadRoleImage(roleId: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<{ success: boolean; data: { image_url: string } }>(
    `/roles/${roleId}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data.image_url;
}

/**
 * Get current user's profile with permissions.
 *
 * GET `/auth/me` (note: not under `/roles`). Useful for client-side permission
 * gating in the admin panel.
 * @returns the authenticated user's id, email, role name, and permission list.
 */
export async function getMyProfile(): Promise<{
  userId: string;
  email: string;
  role: string;
  permissions: string[];
}> {
  const { data } = await apiClient.get<{
    success: boolean;
    data: { userId: string; email: string; role: string; permissions: string[] };
  }>('/auth/me');
  return data.data;
}


/** PATCH /roles/:id/status — toggle role active/inactive */
export async function toggleRoleStatus(id: string, status: 'active' | 'inactive'): Promise<Role> {
  const { data } = await apiClient.patch<{ success: boolean; data: Role }>(`/roles/${id}/status`, { status });
  return data.data;
}
